type SanitizeOptions = {
  ALLOWED_TAGS?: string[];
  ALLOWED_ATTR?: string[];
};

export type SsrTagClassMap = Record<string, string>;

const mergeClasses = (existing: string | undefined, additions: string): string => {
  const classes = new Set([
    ...(existing ?? "").split(/\s+/).filter(Boolean),
    ...additions.split(/\s+/).filter(Boolean),
  ]);
  return Array.from(classes).join(" ");
};

const setTagClasses = (
  html: string,
  tagName: string,
  classes: string
): string => {
  const pattern = new RegExp(`<${tagName}(\\s[^>]*?)?(\\s*/)?>`, "gi");

  return html.replace(pattern, (match, attrs = "", selfClose = "") => {
    const classMatch = attrs.match(/\bclass=(["'])(.*?)\1/i);

    if (classMatch) {
      const merged = mergeClasses(classMatch[2], classes);
      const updatedAttrs = attrs.replace(
        classMatch[0],
        `class="${merged}"`
      );
      return `<${tagName}${updatedAttrs}${selfClose}>`;
    }

    return `<${tagName}${attrs} class="${classes}"${selfClose}>`;
  });
};

const stripDisallowedTags = (html: string, allowedTags: string[]): string => {
  const allowed = new Set(allowedTags.map((tag) => tag.toLowerCase()));
  const tagPattern = /<\/?([a-zA-Z0-9:-]+)(?:\s[^>]*)?>/g;

  return html.replace(tagPattern, (match, tagName: string) => {
    if (allowed.has(tagName.toLowerCase())) {
      return match;
    }
    return "";
  });
};

// Regex-based mirror of a component's own DOMPurify hook, for SSR where jsdom cannot run in V8.
export const sanitizeRteForSsr = (
  html: string,
  tagClassMap: SsrTagClassMap = {},
  options?: SanitizeOptions
): string => {
  let output = html;

  for (const [tagName, classes] of Object.entries(tagClassMap)) {
    output = setTagClasses(output, tagName, classes);
  }

  if (options?.ALLOWED_TAGS?.length) {
    output = stripDisallowedTags(output, options.ALLOWED_TAGS);
  }

  return output;
};
